import { useEffect, useState } from "react";
import { db } from "../Firebase/firebase-config";
import { ref, onValue, update, set } from "firebase/database";
import { Truck, AlertCircle, CheckCircle, X, Info, ExternalLink, ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useNotifications } from "./transportNotifications";
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId";
import "./ClientQuotes.css";

export default function ClientQuotes() {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newRate, setNewRate] = useState("");
  const [clientName, setClientName] = useState("Client");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPageInfo, setShowPageInfo] = useState(false);
  const [toast, setToast] = useState(null);
const [confirmOrderData, setConfirmOrderData] = useState(null);
  const { sendNotification } = useNotifications();
  const { userId: currentClientId } = useGetCurrentUserId();
    const navigate = useNavigate();

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };
 // Fetch Client Name
  useEffect(() => {
    if (!currentClientId) return;
    const clientRef = ref(db, `profileDashboard/${currentClientId}`);
    const unsubscribe = onValue(clientRef, (snapshot) => {
      if (snapshot.exists()) {
        setClientName(snapshot.val().company_name || "Client");
      }
    });
    return () => unsubscribe();
  }, [currentClientId]);
  // Fetch Requests
  useEffect(() => {
    if (!currentClientId) return;

    const requestsRef = ref(db, "transport_requests");
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setRequests([]);
        return;
      }

      const allRequests = [];
      Object.keys(data).forEach((transporterUid) => {
        const transporterRequests = data[transporterUid];
        Object.keys(transporterRequests).forEach((reqKey) => {
          const req = transporterRequests[reqKey];
          if (req.clientId === currentClientId || req.userId === currentClientId) {
            allRequests.push({
              ...req,
              orderKey: reqKey,
              id: reqKey,
              transporterId: transporterUid,
              requestPath: `transport_requests/${transporterUid}/${reqKey}`,
            });
          }
        });
      });

      const sortedRequests = allRequests.sort((a, b) => {
  return (b.createdAt || 0) - (a.createdAt || 0);
});

setRequests(sortedRequests);
    });

    return () => unsubscribe();
  }, [currentClientId]);

  // Filter
  useEffect(() => {
    let result = requests;
    if (filterStatus !== "All") {
      result = requests.filter((r) =>
        r.status?.toLowerCase() === filterStatus.toLowerCase()
      );
    }
   const sorted = result.sort((a, b) => {
  return (b.createdAt || 0) - (a.createdAt || 0);
});

setFilteredRequests(sorted);
  }, [requests, filterStatus]);

  const formatIndianTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kolkata",
    }).format(new Date(timestamp));
  };

  const getLatestRates = (rates = []) => {
  const sorted = [...rates].reverse();

  const finalRate = sorted.find((r) => r.isFinal);

  // If final rate exists, don't return normal transporter/client rates
  if (finalRate) {
    return {
      finalRate,
      transporterRate: null,
      clientRate: null,
    };
  }

  return {
    finalRate: null,
    transporterRate: sorted.find((r) => r.by === "transporter"),
    clientRate: sorted.find((r) => r.by === "client"),
  };
};
  const handleRateSubmit = async () => {
    if (!selectedRequest || !newRate) {
      showToast("Please enter a valid rate", "error");
      return;
    }

    const rate = parseInt(newRate);
    if (isNaN(rate) || rate <= 0) {
      showToast("Please enter a valid amount", "error");
      return;
    }

    setLoading(true);
    const currentRates = selectedRequest.freightRates || [];

    const updatedRates = [
      ...currentRates,
      {
        rate,
        by: "client",
        timestamp: Date.now(),
        // No GST or isFinal for client side as per your requirement
      },
    ];

    try {
      const requestRef = ref(db, selectedRequest.requestPath);
      await update(requestRef, {
        freightRates: updatedRates,
        lastUpdated: Date.now(),
      });

      showToast("Rate sent successfully!", "success");

      setIsModalOpen(false);
      setNewRate("");

      await sendClientRateNotification(rate);
    } catch (error) {
      console.error(error);
      showToast("Failed to send rate", "error");
    } finally {
      setLoading(false);
    }
  };

  const sendClientRateNotification = async (rate) => {
    if (!selectedRequest?.transporterId) return;

    await sendNotification({
      targetUserId: selectedRequest.transporterId,
      pageTitle: "Quote Request",
      title: "New Rate",
    message: `${clientName} has proposed a negotiated rate of ₹${rate} for transportation from ${selectedRequest.from} to ${selectedRequest.to}.`,
     seen:false,
      gifUrl :"/Images/New notification.gif",
      category: "quote",
      isSupplier: false,
    });
  };

  const openQuoteModal = (request) => {
    setSelectedRequest(request);
    setNewRate("");
    setIsModalOpen(true);
  };

  const handlePlaceOrder = async (request) => {
  if (!request.transporterId) {
    showToast("Transporter ID missing", "error");
    return;
  }

  // Generate Order ID: First 3 letters of Company Name + Timestamp
  const companyInitials = clientName
  ? clientName
      .trim()
      .replace(/\s+/g, '') // remove spaces
      .toUpperCase()
      .slice(0, 3)
  : "ORD";
  const timestamp = Date.now().toString(36).toUpperCase();
  const generatedOrderId = `${companyInitials}-${timestamp}`;

  try {
    const orderRef = ref(db, `transport_orders/${request.transporterId}/${request.id}`);
    await set(orderRef, {
      ...request,
      orderId: generatedOrderId,
      orderCreatedAt: Date.now(),
      status: "In-Process",
      placedByClient: currentClientId,
    });

    await update(ref(db, request.requestPath), {
      status: "In-Process",
      orderId: generatedOrderId,
    });

    await sendNotification({
      targetUserId: request.transporterId,
      pageTitle: "Order",
      title: "Order Placed",
      seen: false,
      gifUrl: "/Images/New notification.gif",
      message: `${clientName} placed an order for ${request.from} to ${request.to}.`,
      category: "order",
      isSupplier: false,
    });

    showToast("Order placed successfully!", "success");
  } catch (error) {
    console.error(error);
    showToast("Failed to place order", "error");
  }
};

  return (
    <div className="cq-container">
       <button
          className="trans-back-button"
          onClick={() => navigate("/dashboard/transport")}
        >
          <ArrowLeft size={20} />
          Back to  Dashboard
        </button>
      <div className="cq-header">
        <button onClick={() => setShowPageInfo(!showPageInfo)} className="cq-info-btn">
          <Info size={22} />
        </button>

        <div className="cq-page-title">
          <Truck />
          My Load Requests
        </div>
        <p style={{ textAlign: "center", color: "#64748b", marginTop: "8px" }}>
          Negotiate with transporters
        </p>
      </div>

      {/* Page Info */}
      {showPageInfo && (
        <div className="cq-page-info-modal">
          <div className="cq-page-info-content">
            <button onClick={() => setShowPageInfo(false)} style={{ float: "right", fontSize: "24px", color: "red",border:'none',background:'none' }}>✕</button>
            <h3 style={{ fontSize: "22px", fontWeight: "600", marginBottom: "16px" }}>How Negotiation Works</h3>
            <ul style={{ paddingLeft: "20px", lineHeight: "1.6" }}>
              <li>• Transporters send quotes</li>
              <li>• You can send your rate</li>
              <li>• When final rate is received, place the order</li>
            </ul>
          </div>
        </div>
      )}

      {/* Filter + Add New Button */}
      <div className="cq-filter-container">
       <Link to="/dashboard/new-quote" className="cq-new-quote-btn">
  + New Request
</Link>
        <span style={{ fontWeight: "500", color: "#64748b" }}>Filter:</span>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: "12px", border: "1px solid #e2e8f0" }}
        >
          <option value="All">All</option>
          <option value="waiting for response">Waiting</option>
          <option value="In-Process">In Process</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      <div className="cq-table-container">
        {/* Desktop Table */}
        <div className="cq-table-wrapper">
        <table className="cq-desktop-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Transporter</th>
              <th>Route</th>
              <th>Quantity</th>
              <th>Rate</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>
                  No requests found
                </td>
              </tr>
            ) : (
              filteredRequests.map((req) => {
                const isClosed = req.status === "closed";
                const isInProcess = req.status === "In-Process";
               const { finalRate, transporterRate, clientRate } =
  getLatestRates(req.freightRates);

                return (
                  <tr key={req.id}>
                    <td>{formatIndianTime(req.createdAt)}</td>
                    <td style={{ fontWeight: "500" }}>{req.transporterName || "N/A"}</td>
                    <td>{req.from} → {req.to}</td>
                    <td style={{ textAlign: "center" }}>{req.quantity} {req.quantityUnit}</td>
                 <td>
  {(() => {
    const rates = req.freightRates || [];

    const finalRate = [...rates]
      .reverse()
      .find((r) => r.isFinal);

    // Final Rate
    if (finalRate) {
      return (
        <div>
          <div>
            <strong>₹{finalRate.rate}</strong>
          </div>

          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              marginTop: "2px",
            }}
          >
            {finalRate.includeGST
              ? "Including GST"
              : "Excluding GST"}
          </div>

          <div
            style={{
              fontSize: "11px",
              color: "#10b981",
              fontWeight: "600",
              marginTop: "2px",
            }}
          >
            Final Rate
          </div>
        </div>
      );
    }

    const { transporterRate, clientRate } =
      getLatestRates(rates);

    return (
      <>
        {transporterRate && (
          <div style={{ marginBottom: "4px" }}>
            <div>
              <strong>Trans: ₹{transporterRate.rate}</strong>
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "#64748b",
                marginTop: "2px",
              }}
            >
              {transporterRate.includeGST
                ? "Including GST"
                : "Excluding GST"}
            </div>
          </div>
        )}

        {clientRate && (
          <div
            style={{
              fontSize: "13px",
              color: "#2563eb",
            }}
          >
            You: ₹{clientRate.rate}
          </div>
        )}

        {!transporterRate && !clientRate && "Awaiting"}
      </>
    );
  })()}
</td>
                    <td>
  <span
    style={{
      display: "inline-block",
      padding: "5px 12px",
      borderRadius: "9999px",
      fontSize: "13px",
      fontWeight: "500",
      whiteSpace: "nowrap",
      lineHeight: "1.2",
      backgroundColor: isInProcess
        ? "#d1fae5"
        : isClosed
        ? "#e2e8f0"
        : "#fef3c7",
      color: isInProcess
        ? "#10b981"
        : isClosed
        ? "#475569"
        : "#d97706",
    }}
  >
    {req.status?.replace("-", " ") || "Pending"}
  </span>
</td>
                    <td style={{ textAlign: "right" }}>
                      {isClosed || isInProcess ? (
                        <div>
                          {isInProcess && req.orderId && (
                            <Link to={`/dashboard/transport/orders?orderId=${req.orderId}`} style={{ color: "#4f46e5", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                              View Order <ExternalLink size={14} />
                            </Link>
                          )}
                        {isClosed && (
  <button
    onClick={() => setConfirmOrderData(req)}
    className="cq-po-btn"
  >
    Place Order
  </button>
)}
                        </div>
                      ) : (
                        <button onClick={() => openQuoteModal(req)} style={{
                          padding: "8px 16px", background: "#4f46e5", color: "white",
                          border: "none", borderRadius: "12px", cursor: "pointer"
                        }}>
                          Negotiate
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>


        {/* Mobile View */}
        <div className="cq-mobile-cards">
          {filteredRequests.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>No requests found</div>
          ) : (
            filteredRequests.map((req) => {
              const isClosed = req.status === "closed";
              const isInProcess = req.status === "In-Process";
             const { finalRate, transporterRate, clientRate } =
  getLatestRates(req.freightRates);

              return (
                <div key={req.id} className="cq-mobile-card">
                 <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "10px",
    marginBottom: "12px",
  }}
>
                    <div>
                      <div style={{ fontWeight: "600" }}>{req.transporterName}</div>
                      <div style={{ fontSize: "13px", color: "#64748b" }}>{formatIndianTime(req.createdAt)}</div>
                    </div>
                   <span
  style={{
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 12px",
    borderRadius: "9999px",
    fontSize: "13px",
    fontWeight: "500",
    whiteSpace: "nowrap",
    lineHeight: "1.2",
    flexShrink: 0,
    backgroundColor: isInProcess
      ? "#d1fae5"
      : isClosed
      ? "#e2e8f0"
      : "#fef3c7",
    color: isInProcess
      ? "#10b981"
      : isClosed
      ? "#475569"
      : "#d97706",
  }}
>
  {req.status?.replace("-", " ") || "Pending"}
</span>
                  </div>

                  <div style={{ marginBottom: "12px", fontSize: "15px" }}>
                    {req.from} → {req.to}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
                    <div>{req.quantity} {req.quantityUnit}</div>
                  <div style={{ textAlign: "right" }}>
  {finalRate ? (
    <div>
      <div>
        <strong>₹{finalRate.rate}</strong>

        <span
          style={{
            fontSize: "12px",
            marginLeft: "6px",
            color: "#64748b",
          }}
        >
          {finalRate.includeGST
            ? "(Incl. GST)"
            : "(Excl. GST)"}
        </span>
      </div>

      <div
        style={{
          fontSize: "12px",
          color: "#10b981",
          fontWeight: "500",
          marginTop: "2px",
        }}
      >
        Final Rate
      </div>
    </div>
  ) : (
    <>
      {transporterRate && (
        <div>
          <strong>₹{transporterRate.rate}</strong>

          <span
            style={{
              fontSize: "12px",
              marginLeft: "6px",
              color: "#64748b",
            }}
          >
            {transporterRate.includeGST
              ? "(Incl. GST)"
              : "(Excl. GST)"}
          </span>
        </div>
      )}

      {clientRate && (
        <div
          style={{
            color: "#2563eb",
            fontSize: "13px",
          }}
        >
          You: ₹{clientRate.rate}
        </div>
      )}

      {!transporterRate && !clientRate && (
        <span style={{ color: "#94a3b8" }}>
          Awaiting
        </span>
      )}
    </>
  )}
</div>
                  </div>

                  <div>
                    {isClosed ? (
                      <button
    onClick={() => setConfirmOrderData(req)}
    className="cq-po-btn"
  >
    Place Order
  </button>
                    ) : isInProcess ? (
                      req.orderId && <Link to={`/dashboard/transport-orders?orderId=${req.orderId}`} style={{ color: "#4f46e5", display: "flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>View Order <ExternalLink size={16} /></Link>
                    ) : (
                     <div style={{ display: "flex", justifyContent: "flex-end" }}>
  <button
    onClick={() => openQuoteModal(req)}
    style={{
      padding: "8px 16px",
      background: "#4f46e5",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontWeight: "600",
      cursor: "pointer"
    }}
  >
    Negotiate
  </button>
</div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {confirmOrderData && (
  <div className="cq-po-overlay">
    <div className="cq-po-modal">

      <h3 className="cq-po-title">Confirm Order</h3>

      <p className="cq-po-text">
        Are you sure you want to place this order?
      </p>

      <div className="cq-po-actions">
       

        <button
          className="cq-po-confirm"
          onClick={() => {
            handlePlaceOrder(confirmOrderData);
            setConfirmOrderData(null);
          }}
        >
          Confirm
        </button>
         <button
          className="cq-po-cancel"
          onClick={() => setConfirmOrderData(null)}
        >
          Cancel
        </button>
      </div>

    </div>
  </div>
)}

      {/* Simplified Quote Modal */}
      {isModalOpen && selectedRequest && (
        <div className="cq-modal">
          <div className="cq-modal-content">
            <h3 style={{ fontSize: "24px", fontWeight: "700", marginBottom: "20px" }}>Send Rate</h3>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "6px", color: "#64748b" }}>Route</label>
              <p style={{ fontWeight: "500" }}>{selectedRequest.from} → {selectedRequest.to}</p>
            </div>

            {/* Latest Rates */}
            <div className="cq-rate-history">
              <p style={{ fontWeight: "600", marginBottom: "12px" }}>Latest Rates</p>
              {(() => {
                const { transporterRate, clientRate } = getLatestRates(selectedRequest.freightRates);
                return (
                  <>
                    {transporterRate && (
                      <div className="cq-rate-item">
                        <span>Transporter</span>
                        <span>₹{transporterRate.rate} {transporterRate.includeGST ? "(Incl. GST)" : "(Excl. GST)"}</span>
                      </div>
                    )}
                    {clientRate && (
                      <div className="cq-rate-item">
                        <span>You</span>
                        <span>₹{clientRate.rate}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{ display: "block", marginBottom: "8px", color: "#64748b" }}>Your Freight Rate (₹)</label>
              <input
                type="number"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                onWheel={(e)=>{e.target.blur()}}
                placeholder="Enter amount"
                style={{ width: "100%", fontSize: "18px", fontWeight: "600", padding: "16px", border: "2px solid #e2e8f0", borderRadius: "16px", outline: "none" }}
              />
            </div>

            <button
              onClick={handleRateSubmit}
              disabled={loading || !newRate}
              style={{ width: "100%", padding: "16px", fontSize: "18px", fontWeight: "600", background: "#4f46e5", color: "white", border: "none", borderRadius: "10px", cursor: loading || !newRate ? "not-allowed" : "pointer", opacity: loading || !newRate ? "0.7" : "1" }}
            >
              {loading ? "Sending..." : "Send Rate"}
            </button>

            <button
  onClick={() => setIsModalOpen(false)}
  className="cq-cancel-btn"
>
  Cancel
</button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="cq-toast" style={{ backgroundColor: toast.type === "success" ? "#10b981" : "#ef4444" }}>
          {toast.type === "success" ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}